import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import BarChart from '@mui/icons-material/BarChart';
import Favorite from '@mui/icons-material/Favorite';
import PlayArrow from '@mui/icons-material/PlayArrow';

import { colors } from '../../theme';
import styles from './inviteStyle';

// Fixed pictures, not the real ranking: the popup never depends on a week having a ranking.
const CLIPS = [
  { key: 'left', src: '/img/invite/invite-clip-left.jpg', sx: styles.clipLeft, pillSx: styles.pillLeft, rank: 2, color: colors.rankSilver },
  { key: 'right', src: '/img/invite/invite-clip-right.jpg', sx: styles.clipRight, pillSx: styles.pillRight, rank: 3, color: colors.rankBronze },
  { key: 'center', src: '/img/invite/invite-clip-center.jpg', sx: styles.clipCenter, pillSx: styles.pillCenter, rank: 1, color: colors.top },
];

const REASONS = [
  { key: 'reasonLike', Icon: Favorite, color: '#F44336' },
  { key: 'reasonPost', Icon: Add, color: '#FFFFFF' },
  { key: 'reasonTop', Icon: BarChart, color: '#FFD700' },
];

export type InviteVariant = 'clips' | 'count';

interface InviteContentProps {
  onLater: () => void;
  onLogin: () => void;
  onSignUp: () => void;
  variant: InviteVariant;
  // The videos watched so far, shown by the "count" variant.
  videos: number;
}

// The invitation to sign up of a visitor: three clips, or the number of clips watched and three reasons.
const InviteContent: React.FC<InviteContentProps> = React.memo((props) => {
  const {
    onLater,
    onLogin,
    onSignUp,
    variant,
    videos,
  } = props;
  const { t } = useTranslation();
  const isClips = variant === 'clips';

  return (
    <Box sx={styles.content}>
      <Typography sx={styles.header}>{t('admin.guest.invite.header')}</Typography>
      {isClips ? (
        <Box sx={styles.fan}>
          <Box sx={styles.glow} />
          {CLIPS.map((clip) => (
            <Box key={clip.key} sx={[styles.clip, clip.sx]}>
              <Box component="img" src={clip.src} alt="" sx={styles.clipImage} />
              <Box component="span" sx={[styles.pill, clip.pillSx, { color: clip.color }]}>
                {`Top ${clip.rank}`}
              </Box>
              <Box sx={styles.play}><PlayArrow sx={styles.playIcon} /></Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={styles.ring}>
          <Box sx={styles.ringInner}>
            <Typography sx={styles.ringCount}>{videos}</Typography>
            <Typography sx={styles.ringLabel}>{t('admin.guest.invite.countLabel')}</Typography>
          </Box>
        </Box>
      )}
      <Typography component="h2" sx={[styles.title, isClips && styles.titleClips]}>
        {t(isClips ? 'admin.guest.invite.clipsTitle' : 'admin.guest.invite.countTitle')}
      </Typography>
      <Typography sx={[styles.text, isClips && styles.textClips]}>
        {t(isClips ? 'admin.guest.invite.clipsText' : 'admin.guest.invite.countText')}
      </Typography>
      {!isClips && (
        <Box sx={styles.reasons}>
          {REASONS.map(({ key, Icon, color }) => (
            <Box key={key} sx={styles.reason}>
              <Box sx={styles.reasonIcon}><Icon sx={{ fontSize: 17, color }} /></Box>
              {t(`admin.guest.invite.${key}`)}
            </Box>
          ))}
        </Box>
      )}
      <Button onClick={onSignUp} sx={styles.primary}>{t('admin.guest.createAccount')}</Button>
      <Button onClick={onLogin} sx={styles.secondary}>{t('admin.guest.signIn')}</Button>
      <Box component="button" type="button" onClick={onLater} sx={styles.later}>{t('admin.guest.later')}</Box>
    </Box>
  );
});

export default InviteContent;
